import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useEffect, useRef } from 'react';

import { sanitizeEditorRichText, toRichTextHtml } from './utils/richText';

interface RichTextCardEditorProps {
  initialValue: string;
  onChange: (value: string) => void;
}

export function RichTextCardEditor({ initialValue, onChange }: RichTextCardEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialHtml = toRichTextHtml(initialValue);

    if (editorRef.current && editorRef.current.innerHTML !== initialHtml) {
      editorRef.current.innerHTML = initialHtml;
    }
  }, [initialValue]);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      editorRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, []);

  useEffect(() => {
    if (typeof document.execCommand === 'function') {
      document.execCommand('defaultParagraphSeparator', false, 'div');
    }
  }, []);

  const execute = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand?.(command, false, value);
    onChange(sanitizeEditorRichText(editorRef.current?.innerHTML ?? ''));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 260 }}>
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.55)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.25,
          p: 0.5,
        }}
      >
        <ToolbarButton label="Bold" onClick={() => execute('bold')}>
          <FormatBoldIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Italic" onClick={() => execute('italic')}>
          <FormatItalicIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Underline" onClick={() => execute('underline')}>
          <FormatUnderlinedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Align left" onClick={() => execute('justifyLeft')}>
          <FormatAlignLeftIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Align center" onClick={() => execute('justifyCenter')}>
          <FormatAlignCenterIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Align right" onClick={() => execute('justifyRight')}>
          <FormatAlignRightIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Bulleted list" onClick={() => execute('insertUnorderedList')}>
          <FormatListBulletedIcon fontSize="small" />
        </ToolbarButton>
        <ToolbarButton label="Numbered list" onClick={() => execute('insertOrderedList')}>
          <FormatListNumberedIcon fontSize="small" />
        </ToolbarButton>
        <ColorCommand label="Text color" onChange={(color) => execute('foreColor', color)} />
        <ColorCommand label="Highlight color" onChange={(color) => execute('backColor', color)} />
      </Box>
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={(event) => onChange(sanitizeEditorRichText(event.currentTarget.innerHTML))}
        sx={{
          flex: 1,
          fontSize: 28,
          lineHeight: 1.25,
          minHeight: 0,
          outline: 0,
          overflowY: 'auto',
          p: 1,
          wordBreak: 'break-word',
          '& ul, & ol': {
            marginBlock: 0,
            paddingInlineStart: '1.25em',
          },
          '& p': {
            marginBlock: 0,
          },
        }}
      />
    </Box>
  );
}

function ToolbarButton({
  children,
  label,
  onClick,
}: {
  children: JSX.Element;
  label: string;
  onClick: () => void;
}) {
  return (
    <Tooltip title={label}>
      <IconButton aria-label={label} onMouseDown={(event) => event.preventDefault()} onClick={onClick} size="small">
        {children}
      </IconButton>
    </Tooltip>
  );
}

function ColorCommand({ label, onChange }: { label: string; onChange: (color: string) => void }) {
  return (
    <Tooltip title={label}>
      <Box
        component="label"
        aria-label={label}
        sx={{
          border: '1px solid rgba(0, 0, 0, 0.24)',
          borderRadius: '50%',
          cursor: 'pointer',
          height: 24,
          mx: 0.25,
          overflow: 'hidden',
          width: 24,
        }}
      >
        <Box
          component="input"
          type="color"
          onMouseDown={(event) => event.preventDefault()}
          onChange={(event) => onChange(event.target.value)}
          sx={{
            border: 0,
            cursor: 'pointer',
            height: 34,
            ml: -0.5,
            mt: -0.5,
            p: 0,
            width: 34,
          }}
        />
      </Box>
    </Tooltip>
  );
}
